import { getApiUrl } from './serverConfig'
import { rewriteLegacyStorageHost } from './legacyStorageHost'

function stripStorageVersionPrefix(storagePath: string): string {
  return storagePath.replace(/^v\d+\.\d+\//i, '')
}

/**
 * Convert a storage URI/URL into a same-origin fetch URL that avoids browser CORS issues.
 *
 * For Martini-managed storage paths, this rewrites requests through the backend `/storage/*`
 * endpoint and then through a Next.js proxy route so the browser can safely read the bytes.
 * Blob/data URLs are returned unchanged because they are already locally fetchable.
 */
export function getFetchableStorageUrl(uriOrUrl: string): string {
  if (uriOrUrl.startsWith('blob:') || uriOrUrl.startsWith('data:')) {
    return uriOrUrl
  }

  const versionedStoragePathPattern = /^\/(v\d+\.\d+\/(?:assets|tmp|sets|exports|projects)\/.+)$/i
  let upstreamUrl: string

  if (uriOrUrl.startsWith('/storage/')) {
    upstreamUrl = `${getApiUrl()}${uriOrUrl}`
  } else if (!uriOrUrl.startsWith('http://') && !uriOrUrl.startsWith('https://')) {
    const trimmed = uriOrUrl.startsWith('/') ? uriOrUrl.slice(1) : uriOrUrl
    upstreamUrl = `${getApiUrl()}/storage/${stripStorageVersionPrefix(trimmed)}`
  } else {
    // Only rewrite when we actually use the rewritten URL — otherwise the Sentry
    // signal would fire for storage-path URLs that get replaced with a backend URL.
    let mapped: string | null = null
    try {
      const parsed = new URL(uriOrUrl)
      const storagePathMatch = parsed.pathname.match(versionedStoragePathPattern)
      if (storagePathMatch) {
        mapped = `${getApiUrl()}/storage/${stripStorageVersionPrefix(storagePathMatch[1])}`
      }
    } catch {
      // Fall through to the original URL if parsing fails.
    }
    upstreamUrl = mapped ?? rewriteLegacyStorageHost(uriOrUrl, 'getFetchableStorageUrl')
  }

  return `/api/proxy-media?url=${encodeURIComponent(upstreamUrl)}`
}
